import { create } from 'zustand'

import { toast } from '@/shared/lib/toast'

import * as leadsApi from '../api/leads.api'

import { useAdminDashboardStore } from './dashboard.store'

const DEFAULT_LIST_QUERY = {
  page: 1,
  limit: 5,
  search: '',
  source: undefined,
  origin: undefined,
  salesPerson: undefined,
  period: undefined,
  startDate: undefined,
  endDate: undefined,
  status: undefined,
  leadWarmth: undefined,
}

const initialState = {
  leads: [],
  meta: null,
  lastQuery: DEFAULT_LIST_QUERY,
  isLoading: true,
  isSubmitting: false,
  isAssigning: false,
  followUpLoading: {},
  error: null,
}

export const useLeadsStore = create((set, get) => ({
  ...initialState,

  clearError: () =>
    set({
      error: null,
    }),

  fetchLeads: async (params = {}) => {
    const query = {
      ...DEFAULT_LIST_QUERY,
      ...get().lastQuery,
      ...params,
    }

    set((state) => ({
      lastQuery: query,
      isLoading: state.leads.length === 0,
      error: null,
    }))

    try {
      const result = await leadsApi.getLeads({
        page: query.page,
        limit: query.limit,
        search: query.search?.trim() || undefined,
        source: query.source === 'all' ? undefined : query.source,
        origin: query.origin === 'all' ? undefined : query.origin,
        salesPerson:
          query.salesPerson === 'all' ? undefined : query.salesPerson,
        period: query.period === 'all' ? undefined : query.period,
        startDate: query.startDate,
        endDate: query.endDate,
        status: query.status === 'all' ? undefined : query.status,
        leadWarmth: query.leadWarmth === 'all' ? undefined : query.leadWarmth,
      })

      set({
        leads: result.data,
        meta: result.meta,
        isLoading: false,
        error: null,
      })

      return result
    } catch (error) {
      const message = error.message || 'Failed to fetch leads'

      set({
        isLoading: false,
        error: message,
      })

      toast.error(message)
      throw error
    }
  },

  createLead: async (payload) => {
    if (get().isSubmitting) return null

    set({
      isSubmitting: true,
      error: null,
    })

    try {
      const result = await leadsApi.createLead(payload)

      toast.success(result.message)

      await get().fetchLeads({
        page: 1,
        search: '',
      })

      set({ isSubmitting: false })

      return result.data
    } catch (error) {
      const message = error.message || 'Failed to create lead'

      set({
        isSubmitting: false,
        error: message,
      })

      toast.error(message)
      throw error
    }
  },

  updateLead: async (id, payload) => {
    if (get().isSubmitting) return null

    set({
      isSubmitting: true,
      error: null,
    })

    try {
      const result = await leadsApi.updateLead(id, payload)

      toast.success(result?.message || 'Lead updated successfully')

      set((state) => ({
        leads: state.leads.map((lead) => {
          if (String(lead._id || lead.id) !== String(id)) return lead
          const updated = result?.data || payload
          return {
            ...lead,
            ...updated,
            quotation:
              updated?.quotation !== undefined
                ? updated.quotation
                : lead.quotation,
            salesPerson:
              typeof updated?.salesPerson === 'object' &&
              updated?.salesPerson?.name
                ? updated.salesPerson
                : lead.salesPerson,
            createdBy:
              typeof updated?.createdBy === 'object' && updated?.createdBy?.name
                ? updated.createdBy
                : lead.createdBy,
          }
        }),
        isSubmitting: false,
      }))

      // Refresh dashboard & notify all components across application
      void useAdminDashboardStore.getState().fetchDashboard?.()
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('crm:data-changed', {
            detail: { type: 'lead:updated', id },
          }),
        )
      }

      // Background re-fetch to keep meta counts (sources, total) in sync
      void get().fetchLeads()

      return result?.data
    } catch (error) {
      const message = error.message || 'Failed to update lead'

      set({
        isSubmitting: false,
        error: message,
      })

      toast.error(message)
      throw error
    }
  },

  updateLeadStatus: async (id, status) => {
    return get().updateLead(id, { status })
  },

  bulkUploadLeads: async (file) => {
    set({ isSubmitting: true })

    try {
      const result = await leadsApi.bulkUploadLeads(file)

      toast.success(result.message)

      // Refresh the first page after upload
      await get().fetchLeads({
        page: 1,
        limit: 10,
      })

      void useAdminDashboardStore.getState().fetchDashboard?.()
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('crm:data-changed', {
            detail: { type: 'leads:uploaded' },
          }),
        )
      }

      return result
    } catch (error) {
      const message = error.message || 'Failed to upload leads'

      toast.error(message)
      throw error
    } finally {
      set({ isSubmitting: false })
    }
  },

  assignLead: async (leadId, salesPersonId) => {
    if (get().isAssigning) {
      return
    }

    set({
      isAssigning: true,
      error: null,
    })

    try {
      const result = await leadsApi.assignLead(leadId, salesPersonId)

      set((state) => ({
        leads: state.leads.map((lead) => {
          if (String(lead._id || lead.id) !== String(leadId)) return lead
          const updated = result?.data || {}
          return {
            ...lead,
            ...updated,
            quotation:
              updated?.quotation !== undefined
                ? updated.quotation
                : lead.quotation,
            salesPerson:
              typeof updated?.salesPerson === 'object' &&
              updated?.salesPerson?.name
                ? updated.salesPerson
                : lead.salesPerson,
            createdBy:
              typeof updated?.createdBy === 'object' && updated?.createdBy?.name
                ? updated.createdBy
                : lead.createdBy,
          }
        }),
        isAssigning: false,
      }))

      toast.success(result.message || 'Lead assigned successfully')

      // Refresh dashboard & notify all components across application
      void useAdminDashboardStore.getState().fetchDashboard?.()
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('crm:data-changed', {
            detail: { type: 'lead:assigned', leadId, salesPersonId },
          }),
        )
      }

      // Background re-fetch to keep meta counts and filters consistent
      void get().fetchLeads()

      return result.data
    } catch (error) {
      const message = error.message || 'Failed to assign lead'

      set({
        isAssigning: false,
        error: message,
      })

      toast.error(message)
      throw error
    }
  },

  sendFollowUpEmail: async (id) => {
    const leadId = String(id)

    if (get().followUpLoading[leadId]) {
      return
    }

    set((state) => ({
      followUpLoading: {
        ...state.followUpLoading,
        [leadId]: true,
      },
    }))

    try {
      const response = await leadsApi.sendFollowUpEmail(id)

      set((state) => ({
        leads: state.leads.map((lead) =>
          String(lead._id) === leadId
            ? {
                ...lead,
                followUpSent: true,
                followUpConfirmed: false,
                followUpSentAt: new Date().toISOString(),
              }
            : lead,
        ),
        followUpLoading: {
          ...state.followUpLoading,
          [leadId]: false,
        },
      }))

      useAdminDashboardStore.getState().removeLeadFromNotFollowedUp(id)

      toast.success(response?.message || 'Follow-up email sent successfully')

      return response
    } catch (error) {
      const message = error.message || 'Failed to send follow-up email'

      set((state) => ({
        followUpLoading: {
          ...state.followUpLoading,
          [leadId]: false,
        },
      }))

      toast.error(message)
      throw error
    }
  },
  updateLeadFollowUp: (leadId, followUpData) => {
    set((state) => ({
      leads: state.leads.map((lead) =>
        String(lead._id) === String(leadId)
          ? {
              ...lead,
              followUpConfirmed: followUpData.followUpConfirmed,
              followUpConfirmedAt: followUpData.followUpConfirmedAt,
            }
          : lead,
      ),
    }))
  },
}))
